import { Icon } from '@ur-solutions/react-components'
import { useEffect, useRef } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router'
import styled, { createGlobalStyle } from 'styled-components'

/**
 * index.css (and the exported html itself) force height: 100% on html/body,
 * which makes the browser clip everything after the first page when printing.
 */
const PrintGlobalStyle = createGlobalStyle`
  @media print {
    html,
    body,
    #root {
      height: auto !important;
      overflow: visible !important;
    }
  }
`

const Wrapper = styled.div`
  width: 100%;
  padding: 20px;

  .document {
    width: 90vw;
    max-width: 800px;
    margin: 0 auto;
    height: min(calc(90vw * 1.414), calc(800px * 1.414));
    overflow: hidden;
    background-color: white;
    padding: 5mm;
    margin-bottom: 1rem;
  }

  td {
    word-wrap: break-word;
    overflow-wrap: break-word;
    white-space: normal !important;
    page-break-inside: avoid;
  }

  .document th {
    text-align: left;
  }

  @media print {
    padding: 0;
    margin: 0;
    background-color: white;
    .document {
      width: 100%;
      max-width: 100%;
      margin: 0 0;
      height: auto;
      overflow: visible;
    }
  }
`

const ActionWrapper = styled.div`
  position: fixed;
  left: 1rem;
  top: 1rem;
  color: ${props => props.theme.colors.gray1};
  background-color: ${props => props.theme.colors.gray9};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  width: 100px;
  font-size: 18px;
  display: flex;
  justify-content: space-between;
  overflow: hidden;

  & > div {
    width: 50%;
    padding: 1rem;
  }
  & > div:hover {
    background-color: ${props => props.theme.colors.gray8};
    cursor: pointer;
  }
  @media print {
    display: none;
  }
`

const outerHeight = (el: HTMLElement): number => {
  const style = window.getComputedStyle(el)
  return (
    el.getBoundingClientRect().height +
    parseFloat(style.marginTop) +
    parseFloat(style.marginBottom)
  )
}

/**
 * Split every `.document` in the exported html into multiple A4-sized
 * `.document` pages, breaking tables between rows and repeating the table
 * header on each page. The `.document` boxes have a fixed A4 aspect ratio, so
 * without this a table longer than one page overflows its page and is drawn
 * on top of the following document.
 */
const paginateDocuments = (content: HTMLElement) => {
  const documents = Array.from(
    content.querySelectorAll<HTMLElement>('.document')
  )

  for (const doc of documents) {
    // Everything fits on one page (also makes re-runs a no-op).
    if (doc.scrollHeight <= doc.clientHeight) continue

    const style = window.getComputedStyle(doc)
    const pageHeight =
      doc.clientHeight -
      parseFloat(style.paddingTop) -
      parseFloat(style.paddingBottom)

    // Measure everything before any node is moved.
    const blocks = Array.from(doc.children) as HTMLElement[]
    const blockHeights = new Map(
      blocks.map(block => [block, outerHeight(block)])
    )
    const rowHeights = new Map<HTMLElement, number>()
    for (const block of blocks) {
      if (block instanceof HTMLTableElement) {
        for (const row of Array.from(
          block.querySelectorAll<HTMLElement>('tr')
        )) {
          rowHeights.set(row, row.getBoundingClientRect().height)
        }
      }
    }

    const pages: HTMLElement[] = []
    let page: HTMLElement = doc
    let used = Infinity // force a fresh page on first append

    const newPage = () => {
      page = doc.cloneNode(false) as HTMLElement
      pages.push(page)
      used = 0
    }

    const appendBlock = (el: HTMLElement, height: number) => {
      if (used > 0 && used + height > pageHeight) newPage()
      page.appendChild(el)
      used += height
    }

    for (const block of blocks) {
      if (!(block instanceof HTMLTableElement)) {
        appendBlock(block, blockHeights.get(block) ?? 0)
        continue
      }

      const thead = block.querySelector('thead')
      const theadHeight = thead ? outerHeight(thead as HTMLElement) : 0
      const rows = Array.from(block.querySelectorAll<HTMLElement>('tbody > tr'))

      // Reserve room for the header plus the first row so a table is never
      // started on a page it cannot fit a single row on.
      const startTable = (reserve: number): HTMLElement => {
        if (used > 0 && used + theadHeight + reserve > pageHeight) newPage()
        const table = block.cloneNode(false) as HTMLTableElement
        if (thead) table.appendChild(thead.cloneNode(true))
        const tbody = document.createElement('tbody')
        table.appendChild(tbody)
        page.appendChild(table)
        used += theadHeight
        return tbody
      }

      let tbody = startTable(rowHeights.get(rows[0] as HTMLElement) ?? 0)
      let rowsInTbody = 0
      for (const row of rows) {
        const height = rowHeights.get(row) ?? 0
        if (rowsInTbody > 0 && used + height > pageHeight) {
          newPage()
          tbody = startTable(height)
          rowsInTbody = 0
        }
        tbody.appendChild(row)
        used += height
        rowsInTbody++
      }
    }

    doc.replaceWith(...pages)
  }
}

/**
 * This component renders html from string by using dangerouslySetInnerHTML. The html should be sent to the component through history state.
 */
export const HtmlAsPdf: React.JSX.CFC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const contentRef = useRef<HTMLDivElement>(null)

  const state = location.state as null | { html: string }

  useEffect(() => {
    const content = contentRef.current
    if (!content) return
    // Wait one frame so the injected html is laid out before measuring.
    const raf = requestAnimationFrame(() => paginateDocuments(content))
    return () => cancelAnimationFrame(raf)
  }, [state?.html])

  if (!state) return <Navigate to="/" />

  return (
    <Wrapper>
      <PrintGlobalStyle />
      <ActionWrapper>
        <div onClick={() => void navigate(-1)}>
          <Icon icon="arrow-left" />
        </div>

        <div onClick={() => window.print()}>
          <Icon icon="print" />
        </div>
      </ActionWrapper>

      <div
        id="content"
        ref={contentRef}
        dangerouslySetInnerHTML={{ __html: state?.html ?? '' }}
      ></div>
    </Wrapper>
  )
}
